import { useMemo, useState } from "react";
import { Button, Group, Modal, PasswordInput, Stack, Text } from "@mantine/core";

type Props = {
  opened: boolean;
  onClose: () => void;
  onAuthorized: () => void | Promise<void>;
  expectedPassword: string;
  title: string;
  description: string;
  isLoading?: boolean;
};

export default function OrderSyncPasswordModal({
  opened,
  onClose,
  onAuthorized,
  expectedPassword,
  title,
  description,
  isLoading = false,
}: Props) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const canVerify = useMemo(() => password.trim().length > 0 && expectedPassword.trim().length > 0, [password, expectedPassword]);

  const handleClose = () => {
    setPassword("");
    setError(null);
    setVerifying(false);
    onClose();
  };

  const handleVerify = async () => {
    if (!canVerify || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      if (password !== expectedPassword) {
        setError("Incorrect password.");
        return;
      }
      await onAuthorized();
      handleClose();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={600} size="lg">
          {title}
        </Text>
      }
      centered
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="slate.6">
          {description}
        </Text>
        <PasswordInput
          label="Admin action password"
          placeholder="Enter password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          error={error}
          autoFocus
          disabled={isLoading || verifying}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleVerify();
          }}
        />
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={handleClose} disabled={isLoading || verifying}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleVerify} loading={isLoading || verifying} disabled={!canVerify || isLoading}>
            Continue
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
